import React, { useState, useEffect } from 'react'
import { useAuthStore } from '@/stores/auth.store'
import { usersService } from '@/services/users.service'
import { FileData, UpdateUserDto } from '@/types'
import {
  validateEmail,
  validateHexColor,
  validatePassword,
  validatePhone,
} from '@/utils/validation'
import {
  canDownloadApk,
  canEditActorIdentity,
  getInheritedIdentity,
  isAttachedReseller,
  isBillableActor,
} from '@/utils/role-access'
import { AssignedApkSection } from '@/components/apk/AssignedApkSection'
import { getLogoUrlFromFileData } from '@/utils/branding'
import { Input } from '@/components/ui/Input'
import { ColorPickerInput } from '@/components/ui/ColorPickerInput'
import { FileInput } from '@/components/ui/FileInput'
import { Button } from '@/components/ui/Button'
import { toastError, toastSuccess } from '@/utils/toast'
import { ERROR_MESSAGES } from '@/config/constants'
import { Lock, Envelope, Phone } from 'phosphor-react'

interface ProfileFormData {
  firstname: string
  lastname: string
  email: string
  phone: string
  companyName: string
  infos: string
  primaryColor: string
  secondaryColor: string
  logo?: FileData
}

export const ProfilePage: React.FC = () => {
  const currentUser = useAuthStore(state => state.user)
  const setCurrentUser = useAuthStore(state => state.setCurrentUser)

  const showActorFields = canEditActorIdentity(currentUser)
  const showApkDownload = canDownloadApk(currentUser)
  const inheritedIdentity = getInheritedIdentity(currentUser)
  const showInheritedIdentity = isAttachedReseller(currentUser) && inheritedIdentity

  const [profileData, setProfileData] = useState<ProfileFormData>({
    firstname: '',
    lastname: '',
    email: '',
    phone: '',
    companyName: '',
    infos: '',
    primaryColor: '',
    secondaryColor: '',
    logo: undefined,
  })
  const [logoFile, setLogoFile] = useState<File | null>(null)

  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  })

  const [profileErrors, setProfileErrors] = useState<Record<string, string>>({})
  const [passwordErrors, setPasswordErrors] = useState<Record<string, string>>({})
  const [isUpdatingProfile, setIsUpdatingProfile] = useState(false)
  const [isChangingPassword, setIsChangingPassword] = useState(false)

  useEffect(() => {
    if (currentUser) {
      setProfileData({
        firstname: currentUser.firstname,
        lastname: currentUser.lastname,
        email: currentUser.email,
        phone: currentUser.phone,
        companyName: currentUser.companyName ?? '',
        infos: currentUser.infos ?? '',
        primaryColor: currentUser.primaryColor ?? '',
        secondaryColor: currentUser.secondaryColor ?? '',
        logo: currentUser.logo,
      })
      setLogoFile(null)
    }
  }, [currentUser])

  const handleProfileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target
    setProfileData(prev => ({
      ...prev,
      [name]: value,
    }))
    if (profileErrors[name]) {
      setProfileErrors(prev => {
        const newErrors = { ...prev }
        delete newErrors[name]
        return newErrors
      })
    }
  }

  const handleLogoChange = (files: FileList | null) => {
    const file = files?.[0]
    if (!file) return

    const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/svg+xml', 'image/webp']
    if (!allowedTypes.includes(file.type)) {
      toastError('Seuls les fichiers image (PNG, JPG, SVG, WebP) sont autorisés')
      return
    }

    setLogoFile(file)
    const reader = new FileReader()
    reader.onload = () => {
      const base64 = (reader.result as string).split(',')[1]
      setProfileData(prev => ({
        ...prev,
        logo: {
          filename: file.name,
          data: base64,
        },
      }))
    }
    reader.onerror = () => {
      toastError('Erreur lors de la lecture du logo')
    }
    reader.readAsDataURL(file)
  }

  const handleLogoRemove = () => {
    setLogoFile(null)
    setProfileData(prev => ({
      ...prev,
      logo: undefined,
    }))
  }

  const validateProfile = (): boolean => {
    const newErrors: Record<string, string> = {}

    if (!profileData.firstname.trim()) {
      newErrors.firstname = 'Le prénom est requis'
    }

    if (!profileData.lastname.trim()) {
      newErrors.lastname = 'Le nom est requis'
    }

    if (!profileData.email.trim()) {
      newErrors.email = "L'email est requis"
    } else if (!validateEmail(profileData.email)) {
      newErrors.email = 'Email invalide'
    }

    if (!profileData.phone.trim()) {
      newErrors.phone = 'Le téléphone est requis'
    } else if (!validatePhone(profileData.phone)) {
      newErrors.phone = 'Le téléphone doit commencer par 06 ou 07 et contenir 10 chiffres'
    }

    if (showActorFields) {
      if (profileData.primaryColor && !validateHexColor(profileData.primaryColor)) {
        newErrors.primaryColor = 'Couleur invalide (format attendu : #RRGGBB)'
      }

      if (profileData.secondaryColor && !validateHexColor(profileData.secondaryColor)) {
        newErrors.secondaryColor = 'Couleur invalide (format attendu : #RRGGBB)'
      }
    }

    setProfileErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleProfileSubmit = async () => {
    if (!validateProfile() || !currentUser) {
      return
    }

    setIsUpdatingProfile(true)
    try {
      const updateData: UpdateUserDto = {
        firstname: profileData.firstname,
        lastname: profileData.lastname,
        email: profileData.email,
        phone: profileData.phone,
      }

      if (showActorFields) {
        if (profileData.companyName.trim()) {
          updateData.companyName = profileData.companyName
        }
        if (profileData.infos.trim()) {
          updateData.infos = profileData.infos
        }
        if (profileData.primaryColor) {
          updateData.primaryColor = profileData.primaryColor
        }
        if (profileData.secondaryColor) {
          updateData.secondaryColor = profileData.secondaryColor
        }
        if (profileData.logo?.data) {
          updateData.logo = profileData.logo
        }
      }

      const updatedUser = await usersService.updateCurrent(updateData)
      setCurrentUser(updatedUser)
      toastSuccess('Profil mis à jour avec succès')
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Erreur lors de la mise à jour du profil'
      toastError(errorMessage)
    } finally {
      setIsUpdatingProfile(false)
    }
  }

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target
    setPasswordData(prev => ({
      ...prev,
      [name]: value,
    }))
    if (passwordErrors[name]) {
      setPasswordErrors(prev => {
        const newErrors = { ...prev }
        delete newErrors[name]
        return newErrors
      })
    }
  }

  const validatePasswordForm = (): boolean => {
    const newErrors: Record<string, string> = {}

    if (!passwordData.currentPassword) {
      newErrors.currentPassword = 'Le mot de passe actuel est requis'
    }

    if (!passwordData.newPassword) {
      newErrors.newPassword = 'Le nouveau mot de passe est requis'
    } else if (!validatePassword(passwordData.newPassword)) {
      newErrors.newPassword =
        'Le mot de passe doit contenir au moins 8 caractères, une majuscule, une minuscule, un chiffre et un caractère spécial'
    }

    if (!passwordData.confirmPassword) {
      newErrors.confirmPassword = 'La confirmation du mot de passe est requise'
    } else if (passwordData.newPassword !== passwordData.confirmPassword) {
      newErrors.confirmPassword = 'Les mots de passe ne correspondent pas'
    }

    setPasswordErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handlePasswordSubmit = async () => {
    if (!validatePasswordForm() || !currentUser) {
      return
    }

    setIsChangingPassword(true)
    try {
      try {
        const isPasswordValid = await usersService.verifyPassword(passwordData.currentPassword)
        if (!isPasswordValid) {
          setPasswordErrors(prev => ({
            ...prev,
            currentPassword: 'Le mot de passe actuel est incorrect',
          }))
          setIsChangingPassword(false)
          return
        }
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : ''
        if (errorMessage === ERROR_MESSAGES.MISSMATCH_CREDENTIALS) {
          setPasswordErrors(prev => ({
            ...prev,
            currentPassword: 'Le mot de passe actuel est incorrect',
          }))
          setIsChangingPassword(false)
          return
        }
        throw error
      }

      setPasswordErrors(prev => {
        const newErrors = { ...prev }
        delete newErrors.currentPassword
        return newErrors
      })

      const updatedUser = await usersService.updateCurrent({
        password: passwordData.newPassword,
      })
      setCurrentUser(updatedUser)
      setPasswordData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
      })
      toastSuccess('Mot de passe modifié avec succès')
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Erreur lors de la modification du mot de passe'
      toastError(errorMessage)
    } finally {
      setIsChangingPassword(false)
    }
  }

  const inheritedLogoUrl = inheritedIdentity ? getLogoUrlFromFileData(inheritedIdentity.logo) : null

  if (!currentUser) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-[var(--color-text-secondary)]">Chargement...</div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold text-[var(--color-text-primary)] mb-1">Paramètres</h2>
        <p className="text-sm text-[var(--color-text-secondary)]">
          Gérez vos informations personnelles et votre mot de passe
        </p>
      </div>

      {showApkDownload && <AssignedApkSection />}

      <div className="bg-white rounded-lg border border-[var(--color-border)] shadow-sm p-6">
        <h3 className="text-lg font-medium text-[var(--color-text-primary)] mb-4">
          Informations personnelles
        </h3>
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="Prénom"
              type="text"
              name="firstname"
              value={profileData.firstname}
              onChange={handleProfileChange}
              error={profileErrors.firstname}
              required
            />
            <Input
              label="Nom"
              type="text"
              name="lastname"
              value={profileData.lastname}
              onChange={handleProfileChange}
              error={profileErrors.lastname}
              required
            />
          </div>
          <Input
            label="Email"
            type="email"
            name="email"
            value={profileData.email}
            onChange={handleProfileChange}
            error={profileErrors.email}
            leftIcon={<Envelope className="w-5 h-5" />}
            required
          />
          <Input
            label="Téléphone"
            type="tel"
            name="phone"
            value={profileData.phone}
            onChange={handleProfileChange}
            error={profileErrors.phone}
            leftIcon={<Phone className="w-5 h-5" />}
            required
          />
          <div className="flex justify-end">
            <Button onClick={handleProfileSubmit} isLoading={isUpdatingProfile}>
              Enregistrer les modifications
            </Button>
          </div>
        </div>
      </div>

      
      {(showActorFields || (showInheritedIdentity && inheritedIdentity)) && (
        <div className="bg-white rounded-lg border border-[var(--color-border)] shadow-sm p-6">
          {<h3 className="text-lg font-medium text-[var(--color-text-primary)] mb-4">
            {showActorFields ? 'Identité & personnalisation' : 'Identité héritée du grossiste'}
          </h3>}
          <div className="space-y-4">
            {showActorFields && (
              <>
                <Input
                  label="Nom commercial"
                  type="text"
                  name="companyName"
                  value={profileData.companyName}
                  onChange={handleProfileChange}
                  error={profileErrors.companyName}
                  helperText="Nom affiché pour l'identité visuelle (marque blanche)"
                />

                {currentUser.fluxId != null && isBillableActor(currentUser) && (
                  <Input
                    label="Identifiant Flux"
                    type="text"
                    name="fluxId"
                    value={String(currentUser.fluxId)}
                    readOnly
                    disabled
                    helperText="Identifiant de facturation géré par NEO Systems"
                  />
                )}

                <div>
                  <label className="block text-sm font-medium text-[var(--color-text-primary)] mb-2">
                    Informations complémentaires
                  </label>
                  <textarea
                    name="infos"
                    value={profileData.infos}
                    onChange={e => {
                      setProfileData(prev => ({ ...prev, infos: e.target.value }))
                    }}
                    rows={3}
                    className="w-full px-4 py-2.5 text-base border border-[var(--color-border)] rounded-lg transition-all duration-200 focus:outline-none focus:border-[var(--color-primary)]"
                    placeholder="Adresse, coordonnées de facturation, etc."
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <ColorPickerInput
                    label="Couleur principale"
                    value={profileData.primaryColor}
                    onChange={color => {
                      setProfileData(prev => ({ ...prev, primaryColor: color }))
                      if (profileErrors.primaryColor) {
                        setProfileErrors(prev => {
                          const next = { ...prev }
                          delete next.primaryColor
                          return next
                        })
                      }
                    }}
                    error={profileErrors.primaryColor}
                    placeholder="#08b2b3"
                    fallbackColor="#08b2b3"
                    helperText="Sélecteur ou saisie manuelle (#RRGGBB)"
                  />

                  <ColorPickerInput
                    label="Couleur secondaire"
                    value={profileData.secondaryColor}
                    onChange={color => {
                      setProfileData(prev => ({ ...prev, secondaryColor: color }))
                      if (profileErrors.secondaryColor) {
                        setProfileErrors(prev => {
                          const next = { ...prev }
                          delete next.secondaryColor
                          return next
                        })
                      }
                    }}
                    error={profileErrors.secondaryColor}
                    placeholder="#078aa8"
                    fallbackColor="#078aa8"
                    helperText="Sélecteur ou saisie manuelle (#RRGGBB)"
                  />
                </div>

                <div>
                  <FileInput
                    label="Logo"
                    accept="image/png,image/jpeg,image/jpg,image/svg+xml,image/webp"
                    onChange={handleLogoChange}
                    onFileRemove={handleLogoRemove}
                    selectedFiles={logoFile ? [logoFile] : []}
                    storedPreview={logoFile ? undefined : profileData.logo}
                    previewAlt={profileData.companyName || 'Logo'}
                    helperText="Image utilisée pour la personnalisation marque blanche"
                  />
                </div>
              </>
            )}

            {showInheritedIdentity && inheritedIdentity && (
              <>
                <p className="text-sm text-[var(--color-text-secondary)]">
                  Votre identité visuelle est définie par votre grossiste et ne peut pas être modifiée
                  depuis votre profil.
                </p>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm font-medium text-[var(--color-text-secondary)] mb-1">
                      Nom commercial
                    </p>
                    <p className="text-sm text-[var(--color-text-primary)]">
                      {inheritedIdentity.companyName || '—'}
                    </p>
                  </div>
                </div>

                <div className="flex flex-wrap items-center gap-4">
                  {inheritedIdentity.primaryColor && (
                    <div className="flex items-center gap-2">
                      <span
                        className="w-8 h-8 rounded-lg border border-[var(--color-border)]"
                        style={{ backgroundColor: inheritedIdentity.primaryColor }}
                      />
                      <span className="text-sm text-[var(--color-text-primary)]">
                        {inheritedIdentity.primaryColor}
                      </span>
                    </div>
                  )}
                  {inheritedIdentity.secondaryColor && (
                    <div className="flex items-center gap-2">
                      <span
                        className="w-8 h-8 rounded-lg border border-[var(--color-border)]"
                        style={{ backgroundColor: inheritedIdentity.secondaryColor }}
                      />
                      <span className="text-sm text-[var(--color-text-primary)]">
                        {inheritedIdentity.secondaryColor}
                      </span>
                    </div>
                  )}
                </div>

                {inheritedLogoUrl && (
                  <div>
                    <p className="text-sm font-medium text-[var(--color-text-secondary)] mb-2">Logo</p>
                    <img
                      src={inheritedLogoUrl}
                      alt={inheritedIdentity.companyName || 'Logo grossiste'}
                      className="h-16 w-16 object-contain rounded-lg border border-[var(--color-border)]"
                    />
                  </div>
                )}
              </>
            )}

            {showActorFields && <div className="flex justify-end">
              <Button onClick={handleProfileSubmit} isLoading={isUpdatingProfile}>
                Enregistrer les modifications
              </Button>
            </div>}
          </div>
        </div>
      )}

      <div className="bg-white rounded-lg border border-[var(--color-border)] shadow-sm p-6">
        <h3 className="text-lg font-medium text-[var(--color-text-primary)] mb-4">
          Modifier le mot de passe
        </h3>
        <div className="space-y-4">
          <Input
            label="Mot de passe actuel"
            type="password"
            name="currentPassword"
            value={passwordData.currentPassword}
            onChange={handlePasswordChange}
            error={passwordErrors.currentPassword}
            leftIcon={<Lock className="w-5 h-5" />}
            required
          />
          <Input
            label="Nouveau mot de passe"
            type="password"
            name="newPassword"
            value={passwordData.newPassword}
            onChange={handlePasswordChange}
            error={passwordErrors.newPassword}
            leftIcon={<Lock className="w-5 h-5" />}
            helperText="Au moins 8 caractères, une majuscule, une minuscule, un chiffre et un caractère spécial"
            required
          />
          <Input
            label="Confirmer le nouveau mot de passe"
            type="password"
            name="confirmPassword"
            value={passwordData.confirmPassword}
            onChange={handlePasswordChange}
            error={passwordErrors.confirmPassword}
            leftIcon={<Lock className="w-5 h-5" />}
            required
          />
          <div className="flex justify-end">
            <Button onClick={handlePasswordSubmit} isLoading={isChangingPassword}>
              Modifier le mot de passe
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}
